import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  Grid,
  GridItem,
  Skeleton,
  StatusPill,
  StatusPillVariant,
  Text,
  errorColorFor,
} from '@hivewatch/dark'

import {
  ComponentHealthStatus,
  GetComponentStatusChangeQuery,
} from '@/graphql/appsync-component-health'
import { formatDateTime } from '@/utils/formatDateTime'

interface DarkGatewayComponentStatusIProps {
  componentStatusData?: GetComponentStatusChangeQuery
  hasError?: boolean
  isLoading: boolean
}

const STATUS_PILL_VARIANTS: Record<ComponentHealthStatus, StatusPillVariant> = {
  [ComponentHealthStatus.RUNNING]: 'success',
  [ComponentHealthStatus.ERRORED]: 'error',
  [ComponentHealthStatus.BROKEN]: 'error',
  [ComponentHealthStatus.FINISHED]: 'in-progress',
}

/**
 * Rebrand variant of {@link GatewayComponentStatus} built on `@hivewatch/dark`.
 * Same three-column component / last seen / status layout; the legacy badge
 * becomes a `StatusPill`. Rendered inside `DarkGatewayDetailsModal` only.
 */
export const DarkGatewayComponentStatus = ({
  componentStatusData,
  hasError,
  isLoading,
}: DarkGatewayComponentStatusIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const labelColor =
    colorScheme === 'light' ? 'light.text.primary' : 'text.primary'

  if (!isLoading && !componentStatusData && hasError) {
    return (
      <Text
        color={errorColorFor(colorScheme)}
        data-testid='DarkGatewayComponentStatus:error'
        textStyle='label'
      >
        {t('gateways:crud_status_error')}
      </Text>
    )
  }

  const componentName =
    componentStatusData?.getComponentStatusChange?.componentName
  const lastSeen = componentStatusData?.getComponentStatusChange?.lastSeen
  const status =
    componentStatusData?.getComponentStatusChange?.componentHealthStatus

  return (
    <Grid
      alignItems='center'
      data-testid='DarkGatewayComponentStatus:grid'
      gap='8px 4px'
      templateColumns='1fr 160px 110px'
    >
      <GridItem>
        <Text color={labelColor} textStyle='label'>
          {t('gateways:crud_status_col_component')}
        </Text>
      </GridItem>
      <GridItem>
        <Text color={labelColor} textStyle='label'>
          {t('gateways:crud_status_col_last_seen')}
        </Text>
      </GridItem>
      <GridItem>
        <Text color={labelColor} textStyle='label'>
          {t('common:status')}
        </Text>
      </GridItem>
      {isLoading ? (
        <>
          <GridItem>
            <Skeleton h='16px' w='200px' />
          </GridItem>
          <GridItem>
            <Skeleton h='16px' w='140px' />
          </GridItem>
          <GridItem>
            <Skeleton h='16px' w='80px' />
          </GridItem>
        </>
      ) : (
        <>
          <GridItem>
            <Text textStyle='body'>{componentName}</Text>
          </GridItem>
          <GridItem>
            <Text textStyle='body'>{formatDateTime(lastSeen)}</Text>
          </GridItem>
          <GridItem>
            {status && (
              <StatusPill
                colorScheme={colorScheme}
                data-testid='DarkGatewayComponentStatus:status'
                label={status.toLowerCase()}
                textTransform='capitalize'
                variant={STATUS_PILL_VARIANTS[status]}
              />
            )}
          </GridItem>
        </>
      )}
    </Grid>
  )
}
